import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { advGetToken, advClearToken } from "./AdvertiserLoginPage";

const STATUS_BADGE = {
  draft:          { label: "Draft",          color: "#6B7C82" },
  pending_review: { label: "Pending Review", color: "#F59E0B" },
  active:         { label: "Active",         color: "#0AADA0" },
  expired:        { label: "Expired",        color: "#6B7C82" },
  rejected:       { label: "Rejected",       color: "#E63946" },
  deactivated:    { label: "Deactivated",    color: "#6B7C82" },
};

export default function AdvertiserDashboardPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Advertiser Dashboard — IslandsDaily";
    // First: is the module enabled at all?
    api.get("/advertise/enabled").then((r) => {
      if (!r.data?.enabled) { setNotFound(true); return; }
      const token = advGetToken();
      if (!token) { nav("/advertiser/login"); return; }
      Promise.all([
        api.get("/advertiser/me", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/advertiser/campaigns", { headers: { Authorization: `Bearer ${token}` } }),
      ]).then(([m, c]) => {
        setMe(m.data);
        setCampaigns(c.data || []);
        setLoading(false);
      }).catch(() => {
        advClearToken();
        nav("/advertiser/login");
      });
    }).catch(() => setNotFound(true));
  }, [nav]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="adv-dashboard-404">
        <h1 className="font-serif-display text-3xl text-[#1A2E35]">404</h1>
      </div>
    );
  }
  if (loading) return <div className="p-10 text-center text-[#6B7C82]">Loading…</div>;

  const logout = () => { advClearToken(); nav("/advertiser/login"); };

  return (
    <div className="min-h-screen bg-white" data-testid="advertiser-dashboard">
      <Header />
      <main className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif-display text-[#1A2E35] text-2xl">Your Campaigns</h1>
            <p className="text-[13px] text-[#6B7C82]">{me?.business_name || me?.email}</p>
          </div>
          <button onClick={logout} className="text-[12px] text-[#6B7C82]" data-testid="adv-logout">Logout</button>
        </div>

        {campaigns.length === 0 ? (
          <div className="py-16 text-center text-[#6B7C82]" data-testid="adv-empty">
            No campaigns yet.{" "}
            <Link to="/advertise" className="text-[#0AADA0]">Book your first ad →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {campaigns.map((c) => {
              const b = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
              const rate = c.impressions > 0
                ? `${((c.clicks / c.impressions) * 100).toFixed(1)}%`
                : "—";
              return (
                <div key={c.order_id} className="border border-[#EEEEEE] rounded-xl p-4" data-testid={`campaign-${c.order_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[13px] font-medium text-[#1A2E35]">
                      {c.slot_type} · #{c.order_id}
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-white px-2 py-0.5 rounded-full"
                          style={{ background: b.color }}>{b.label}</span>
                  </div>
                  <div className="text-[12px] text-[#6B7C82]">
                    Islands: {(c.island_targeting || []).join(", ")}
                  </div>
                  <div className="text-[12px] text-[#6B7C82]">
                    {c.start_date} → {c.end_date}
                  </div>
                  {c.image_url && (
                    <img src={c.image_url} alt="creative" className="mt-3 rounded-lg max-h-32" />
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[12px]">
                    <div>
                      <div className="text-[#6B7C82]">Impr.</div>
                      <div className="text-[#1A2E35] font-medium">{c.impressions || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[#6B7C82]">Clicks</div>
                      <div className="text-[#1A2E35] font-medium">{c.clicks || 0}</div>
                    </div>
                    <div>
                      <div className="text-[#6B7C82]">CTR</div>
                      <div className="text-[#1A2E35] font-medium">{rate}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button className="text-[12px] text-[#0AADA0]" data-testid={`update-creative-${c.order_id}`}>
                      Update Creative
                    </button>
                    <Link
                      to={`/advertise/book?slot=${c.slot_type}`}
                      className="text-[12px] text-[#0AADA0]"
                      data-testid={`renew-${c.order_id}`}
                    >Renew</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
